import React from 'react';
import { Box, Pagination, PaginationItem, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useLocation } from 'react-router-dom';

/** Reads `?page=` — anything that isn't a whole number ≥ 1 means page 1. */
export function parsePageParam(value: string | null): number {
    if (!value || !/^\d+$/.test(value)) return 1;
    const page = Number(value);
    return Number.isSafeInteger(page) && page >= 1 ? page : 1;
}

/** Same URL with `page` set (page 1 drops the parameter so it keeps a single canonical address). */
export function withPage(search: string, page: number): string {
    const params = new URLSearchParams(search);
    if (page <= 1) {
        params.delete('page');
    } else {
        params.set('page', String(page));
    }
    const query = params.toString();
    return query ? `?${query}` : '';
}

interface FeedPaginationProps {
    page: number;
    pageCount: number;
}

/**
 * Numbered pages for the home feed. Every page is a real link, so pages can be opened in a new tab,
 * bookmarked, shared and walked with the browser's back/forward buttons.
 */
const FeedPagination = ({ page, pageCount }: FeedPaginationProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
    const location = useLocation();

    if (pageCount <= 1) return null;

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', px: 2, pt: 1, pb: 3 }}>
            <Pagination
                aria-label={t('web.pagination-label')}
                page={page}
                count={pageCount}
                color="primary"
                shape="rounded"
                size={isPhone ? 'medium' : 'large'}
                siblingCount={isPhone ? 0 : 1}
                boundaryCount={1}
                sx={{
                    // MUI's default keyboard focus is a faint grey fill; make it unmistakable
                    '& .MuiPaginationItem-root.Mui-focusVisible': {
                        outline: `2px solid ${theme.palette.primary.main}`,
                        outlineOffset: 2
                    }
                }}
                getItemAriaLabel={(type, itemPage) => {
                    if (type === 'next') return t('web.pagination-next');
                    if (type === 'previous') return t('web.pagination-previous');
                    return t('web.pagination-page', { page: itemPage });
                }}
                renderItem={(item) => (
                    <PaginationItem component={RouterLink} to={{ pathname: location.pathname, search: withPage(location.search, item.page ?? 1) }} {...item} />
                )}
            />
        </Box>
    );
};

export default FeedPagination;
